import { EVIDENCE_RANK } from "./types"
import type { Baustein, EvidenceInstrument, RequiredDriver } from "./types"
import type { ResolvedPolicy } from "./policy"

/**
 * What is actually known about one person finishing one Baustein.
 *
 * The engine previously had no such concept: `CompletedBaustein { bausteinId,
 * completedOn }` was the entire record of learning, and `resolveCurrentLevels`
 * credited every completion unconditionally, commented as "a fact of record".
 * That treats attendance as competence.
 *
 * In a regulated industry it is also not defensible. Conduct rules typically
 * require a person to EVIDENCE their knowledge and to re-evidence it on a cycle
 * — an attendance row is not evidence. (The demo dataset uses FIDLEG Art. 6,
 * where the cycle is 24 months, but the requirement is not specific to banking.)
 * So the gap this closes is regulatory before it is pedagogical.
 *
 * The shape follows Kirkpatrick, including the Level 2→3 bridge that the
 * four-level summary usually omits: a deliverable applied to a real decision is
 * neither "learning" nor yet "behaviour", and collapsing it into either loses
 * the step where transfer actually happens.
 *
 * Level 4 is deliberately absent here. Results are ORGANISATIONAL — attributing
 * a business outcome to one person's one course is the mistake the model exists
 * to prevent. It lives on `PopulationSummary` instead.
 */
export interface LearningEvidence {
    bausteinId: string

    /**
     * Level 1 — Reaction.
     *
     * `likelihoodToApply`, not satisfaction. New World Kirkpatrick's own
     * correction: how much someone enjoyed a course predicts nothing, whereas
     * whether they intend to use it predicts transfer. Both 1–5.
     */
    reaction?: { relevance: number; likelihoodToApply: number; on: string }

    /**
     * Level 2 — Learning. The LMS result.
     *
     * `scaled` is 0–1, matching xAPI's `result.score.scaled` and LTI AGS's
     * `scoreGiven / scoreMaximum`, so a real adapter fills this in without a
     * conversion step.
     */
    learning?: {
        instrument: EvidenceInstrument
        scaled: number
        passed: boolean
        on: string
    }

    /** Level 2→3 bridge — a deliverable applied to a real decision or commitment. */
    bridge?: { deliverable: string; meetsCriteria: boolean; on: string }

    /**
     * Level 3 — Behaviour, confirmed in the 14–30 day window.
     *
     * Self-reported and, where feasible, manager-verified — the hedge is in the
     * model because it is true in practice. `drivers` records which of the
     * required drivers the manager actually put in place; they are the half of
     * Level 3 that determines whether the behaviour survives.
     */
    behaviour?: {
        applied: "yes" | "partly" | "no"
        selfReported: boolean
        verifiedBy?: string
        on: string
        drivers: RequiredDriver[]
    }
}

/** Index a person's evidence for lookup by Baustein. */
export function evidenceIndex(
    evidence: LearningEvidence[] | undefined,
): ReadonlyMap<string, LearningEvidence> {
    return new Map((evidence ?? []).map((entry) => [entry.bausteinId, entry]))
}

/**
 * The strongest instrument for which there is a PASSING result.
 *
 * Note what this deliberately does not do: a Baustein declaring
 * `evidence: "wissenstest"` says the course HAS an exam, not that this person
 * passed it. Without a result the only thing proven is attendance. Conflating
 * the two would let a catalogue edit manufacture competence organisation-wide.
 */
export function provenInstrument(
    baustein: Baustein,
    evidence: LearningEvidence | undefined,
    policy: ResolvedPolicy,
): EvidenceInstrument {
    let best: EvidenceInstrument = "abschluss"
    const raise = (candidate: EvidenceInstrument) => {
        if (EVIDENCE_RANK[candidate] > EVIDENCE_RANK[best]) best = candidate
    }

    const learning = evidence?.learning
    if (learning && learning.passed && learning.scaled >= policy.evidence.learningPassScaled) {
        raise(learning.instrument)
    }
    // A completed deliverable that met its criteria is the bridge, whatever the
    // course's own assessment happened to be.
    if (evidence?.bridge?.meetsCriteria) raise("transfernachweis")

    void baustein
    return best
}

/**
 * Does this completion count, under the current policy?
 *
 * At the default floor of `abschluss` (rank 0) this is always true, which is why
 * turning the gates on is the entire difference between Kirkpatrick and LTEM:
 * the transcript does not change, the standard does.
 */
export function clearsEvidenceFloor(
    baustein: Baustein,
    evidence: LearningEvidence | undefined,
    policy: ResolvedPolicy,
): boolean {
    if (EVIDENCE_RANK[provenInstrument(baustein, evidence, policy)] < EVIDENCE_RANK[policy.evidence.floor]) {
        return false
    }
    if (policy.evidence.requireBehaviour) {
        const applied = evidence?.behaviour?.applied
        if (applied !== "yes" && applied !== "partly") return false
    }
    return true
}

/**
 * The highest Kirkpatrick level reached for one completion, 0–3.
 *
 * 0 = nothing recorded beyond the completion itself. Level 4 is never returned:
 * it is not a per-person quantity.
 */
export type KirkpatrickLevel = 0 | 1 | 2 | 3

export function kirkpatrickLevel(
    evidence: LearningEvidence | undefined,
    policy: ResolvedPolicy,
): KirkpatrickLevel {
    if (!evidence) return 0
    if (evidence.behaviour && evidence.behaviour.applied !== "no") return 3
    if (evidence.bridge?.meetsCriteria) return 3
    if (
        evidence.learning &&
        evidence.learning.passed &&
        evidence.learning.scaled >= policy.evidence.learningPassScaled
    ) {
        return 2
    }
    if (evidence.reaction) return 1
    return 0
}

/** Did Level 1 clear its target? The template's "4.0 out of 5 on likelihood to apply". */
export function meetsReactionTarget(
    evidence: LearningEvidence | undefined,
    policy: ResolvedPolicy,
): boolean {
    const reaction = evidence?.reaction
    return reaction !== undefined && reaction.likelihoodToApply >= policy.evidence.reactionTarget
}
